import { ToggleButton, ToggleButtonGroup } from '@mui/material';
import {
  type GridCellParams,
  type GridColDef,
  type GridRenderCellParams,
} from '@mui/x-data-grid-premium';
import { cn } from 'cn';
import {
  type MouseEvent as ReactMouseEvent,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { ErrorBoundary } from '~/atoms';
import { Alert, FormattedNumber } from '~/molecules';

import { DataTable } from '../../../DataTable/DataTable';
import {
  type DataImportCopy,
  type DataImportCopyOverrides,
  useDataImportCopy,
} from '../../dataImport.copy';
import {
  type DataImportPreviewIssue,
  type DataImportPreviewModel,
  type DataImportPreviewRow,
} from '../../dataImport.preview';

type PreviewTableRow = DataImportPreviewRow;

const sourceLineColumnField = '__dataImportSourceLine';
const importValueColumnPrefix = '__dataImportValue';
const pinnedColumns = { left: [sourceLineColumnField] };

function getEstimatedRowHeight() {
  return 57; // Height of a row with two lines b/c the text in a lot of cells wraps
}

function getAutomaticRowHeight() {
  return 'auto' as const;
}

function getPreviewRowId(row: PreviewTableRow) {
  return row.rowIndex;
}

function getRowIssues(
  issues: readonly DataImportPreviewIssue[],
  rowIndex: number,
) {
  return issues.filter((issue) => issue.rowIndex === rowIndex);
}

function getCellIssues(
  rowIssues: readonly DataImportPreviewIssue[],
  field: string | undefined,
  mappedFields: ReadonlySet<string>,
) {
  if (field === undefined) {
    return rowIssues.filter(
      (issue) => !issue.field || !mappedFields.has(issue.field),
    );
  }

  return rowIssues.filter((issue) => issue.field === field);
}

function stringifyPreviewObject(value: object) {
  try {
    return JSON.stringify(value);
  } catch {
    return '—';
  }
}

function getPreviewText(value: unknown) {
  if (value === null || value === undefined || typeof value === 'function') {
    return '—';
  }

  if (typeof value === 'object') {
    return stringifyPreviewObject(value);
  }

  if (typeof value === 'symbol') {
    return value.description;
  }

  return String(value as string | number | boolean | bigint);
}

function PreviewValue({ value }: { readonly value: unknown }) {
  return typeof value === 'number' ? (
    <FormattedNumber value={value} />
  ) : (
    <>{getPreviewText(value)}</>
  );
}

function getIssueKey(issue: DataImportPreviewIssue) {
  return [
    issue.source,
    issue.severity,
    issue.rowIndex ?? 'global',
    issue.field ?? 'row',
    issue.message,
  ].join(':');
}

function PreviewCell({
  issues,
  value,
}: {
  readonly issues: readonly DataImportPreviewIssue[];
  readonly value: unknown;
}) {
  const hasError = issues.some((issue) => issue.severity === 'error');

  return (
    <div
      className={cn(
        'flex w-full min-w-0 flex-col gap-1 py-2 wrap-break-word whitespace-normal',
        hasError && 'text-tiga-semantic-error',
      )}
    >
      <PreviewValue value={value} />
      {issues.map((issue) => (
        <span
          className={cn(
            'text-xs wrap-break-word whitespace-normal',
            issue.severity === 'error' && 'text-tiga-semantic-error',
          )}
          key={getIssueKey(issue)}
        >
          {issue.message}
        </span>
      ))}
    </div>
  );
}

function PreviewFilterToggleButton({
  count,
  label,
  value,
}: {
  readonly count: number;
  readonly label: string;
  readonly value: 'all' | 'errors';
}) {
  return (
    <ToggleButton value={value} className="space-x-1 px-4">
      <span>{label}</span>
      <span aria-hidden>·</span>
      <FormattedNumber value={count} />
    </ToggleButton>
  );
}

function createColumns(
  model: DataImportPreviewModel,
  sourceLineHeader: string,
): GridColDef<PreviewTableRow, unknown>[] {
  const mappedFields = new Set(model.columns.map((column) => column.field));
  const getAssociatedCellIssues = (
    rowIndex: number,
    field: string | undefined,
  ) => getCellIssues(getRowIssues(model.issues, rowIndex), field, mappedFields);
  const cellClassName =
    (field?: string) =>
    ({ row }: GridCellParams<PreviewTableRow, unknown>) => {
      const hasError = getAssociatedCellIssues(row.rowIndex, field).some(
        (issue) => issue.severity === 'error',
      );

      if (field === undefined) {
        return hasError
          ? 'bg-tiga-semantic-error/10'
          : 'bg-tiga-surface-off-white';
      }

      if (hasError) return 'bg-tiga-semantic-error/10';
      return '';
    };

  const renderCell =
    (field?: string) =>
    ({ row, value }: GridRenderCellParams<PreviewTableRow, unknown>) => (
      <PreviewCell
        issues={getAssociatedCellIssues(row.rowIndex, field)}
        value={value}
      />
    );

  return [
    {
      align: 'right',
      cellClassName: cellClassName(),
      field: sourceLineColumnField,
      flex: 0,
      headerAlign: 'right',
      headerClassName: '[&&]:bg-tiga-surface-off-white',
      headerName: sourceLineHeader,
      renderCell: renderCell(),
      sortable: false,
      valueGetter: (_value, row: PreviewTableRow) => row.sourceLine,
      width: 76,
    },
    ...model.columns.map(({ field, sourceHeader }, index) => ({
      cellClassName: cellClassName(field),
      field: `${importValueColumnPrefix}${index}`,
      flex: 1,
      headerClassName: '[&&]:bg-tiga-surface-off-white',
      headerName: sourceHeader,
      minWidth: 160,
      renderCell: renderCell(field),
      valueGetter: (_value, row: PreviewTableRow) => row.values[field],
    })),
  ];
}

/**
 * The row grid and its filter, split out from {@link DataImportPreviewTable}
 * so the public component can wrap it in its own `ErrorBoundary` without
 * resolving copy twice.
 */
function DataImportPreviewTableContent({
  copy,
  model,
  showGlobalIssues = true,
}: {
  readonly copy: DataImportCopy;
  readonly model: DataImportPreviewModel;
  readonly showGlobalIssues?: boolean;
}) {
  const errorRowIndexes = useMemo(
    () =>
      new Set(
        model.issues.flatMap((issue) =>
          issue.rowIndex === undefined || issue.severity !== 'error'
            ? []
            : [issue.rowIndex],
        ),
      ),
    [model.issues],
  );
  const defaultFilter = errorRowIndexes.size > 0 ? 'errors' : 'all';
  const [filterState, setFilterState] = useState<{
    filter: 'all' | 'errors';
    model: DataImportPreviewModel;
  }>({ filter: defaultFilter, model });
  const activeFilter =
    filterState.model === model ? filterState.filter : defaultFilter; // Reset filter when model changes
  const isShowingErrorRows = activeFilter === 'errors';
  const visibleRows = useMemo(
    () =>
      isShowingErrorRows
        ? model.rows.filter((row) => errorRowIndexes.has(row.rowIndex))
        : model.rows,
    [errorRowIndexes, isShowingErrorRows, model.rows],
  );
  const columns = useMemo(
    () => createColumns(model, copy.sourceLineColumn),
    [copy.sourceLineColumn, model],
  );
  const handleFilterChange = useCallback(
    (_event: ReactMouseEvent<HTMLElement>, filter: 'all' | 'errors' | null) => {
      if (filter) setFilterState({ filter, model });
    },
    [model],
  );
  const globalIssues = model.issues.filter(
    (issue) => issue.rowIndex === undefined,
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4">
      {showGlobalIssues
        ? globalIssues.map((issue) => (
            <Alert key={getIssueKey(issue)} severity={issue.severity}>
              <span className="wrap-break-word whitespace-normal">
                {issue.message}
              </span>
            </Alert>
          ))
        : null}

      {errorRowIndexes.size > 0 ? (
        <ToggleButtonGroup
          aria-label={copy.previewFilterLabel}
          className="ml-auto shrink-0"
          color="primary"
          exclusive
          size="small"
          value={activeFilter}
          onChange={handleFilterChange}
        >
          <PreviewFilterToggleButton
            count={model.rows.length}
            label={copy.showAllRows}
            value="all"
          />
          <PreviewFilterToggleButton
            count={errorRowIndexes.size}
            label={copy.showErrorRows}
            value="errors"
          />
        </ToggleButtonGroup>
      ) : null}

      <div className="min-h-0 flex-1 overflow-hidden rounded-md border border-tiga-border-lighter">
        <DataTable
          columnHeaderHeight={40}
          columns={columns}
          getEstimatedRowHeight={getEstimatedRowHeight}
          getRowHeight={getAutomaticRowHeight}
          getRowId={getPreviewRowId}
          height="100%"
          hideFooter
          pagination={false}
          pinnedColumns={pinnedColumns}
          rows={visibleRows}
        />
      </div>
    </div>
  );
}

export function DataImportPreviewTable({
  copy: copyOverrides,
  model,
  showGlobalIssues = true,
}: {
  /** The wizard's already-resolved copy; standalone use falls back to the defaults. */
  readonly copy?: DataImportCopyOverrides;
  readonly model: DataImportPreviewModel;
  readonly showGlobalIssues?: boolean;
}) {
  const copy = useDataImportCopy(copyOverrides);

  return (
    <ErrorBoundary fallbackMessage={copy.previewFallback}>
      <DataImportPreviewTableContent
        copy={copy}
        model={model}
        showGlobalIssues={showGlobalIssues}
      />
    </ErrorBoundary>
  );
}
